import React, { useMemo, useCallback, useRef, useEffect } from 'react';
import ForceGraph2D from 'react-force-graph-2d';
import { Button } from './ui/button';
import { Maximize2, MousePointer2 } from 'lucide-react';

const SEVERITY_FILL = {
  critical: '#dc2626',
  high: '#ea580c',
  medium: '#ca8a04',
  low: '#16a34a',
  info: '#64748b',
  unknown: '#64748b',
};

const GROUP_FALLBACK = {
  Finding: '#38bdf8',
  Asset: '#34d399',
  Host: '#a78bfa',
  CVE: '#f472b6',
  Service: '#fbbf24',
  Node: '#94a3b8',
};

function hashHue(str) {
  let h = 0;
  for (let i = 0; i < str.length; i += 1) h = str.charCodeAt(i) + ((h << 5) - h);
  return Math.abs(h) % 360;
}

function colorForGroup(group) {
  if (GROUP_FALLBACK[group]) return GROUP_FALLBACK[group];
  const hue = hashHue(group || 'x');
  return `hsl(${hue}, 55%, 52%)`;
}

function isFindingNode(labels) {
  return (labels || []).some((l) => String(l).toLowerCase() === 'finding');
}

function pickFill(node) {
  const props = node.properties || {};
  const sev = String(props.severity || '').toLowerCase();
  const labels = node.labels || [];
  if (isFindingNode(labels) && sev && SEVERITY_FILL[sev]) {
    return SEVERITY_FILL[sev];
  }
  return colorForGroup(node.group || 'Node');
}

function truncate(s, max) {
  if (!s) return '';
  const t = String(s).trim();
  return t.length <= max ? t : `${t.slice(0, max - 1)}…`;
}

function primaryLine(node) {
  const props = node.properties || {};
  const raw = props.title || props.name || node.display_label || node.group || 'Node';
  return truncate(raw, 36);
}

function typeLine(node) {
  const labels = node.labels || [];
  const g = node.group || labels[0] || 'Node';
  return labels.length > 1 ? `${labels.join(' · ')}` : g;
}

/**
 * Interactive 2D force layout for Neo4j snapshot from GET /api/graph/visualization.
 */
export function GraphFindingsForceGraph({ graphData, onNodeClick, highlightId }) {
  const fgRef = useRef();

  const { data, stats, legendGroups, legendSeverity } = useMemo(() => {
    const rawNodes = graphData?.nodes || [];
    const rawLinks = graphData?.links || [];
    const ids = new Set(rawNodes.map((n) => n.id));

    const degree = new Map();
    rawNodes.forEach((n) => degree.set(n.id, 0));
    const links = rawLinks
      .filter((l) => ids.has(l.source) && ids.has(l.target))
      .map((l) => {
        degree.set(l.source, (degree.get(l.source) || 0) + 1);
        degree.set(l.target, (degree.get(l.target) || 0) + 1);
        return {
          source: l.source,
          target: l.target,
          name: l.type || 'RELATED',
        };
      });

    const nodes = rawNodes.map((n) => {
      const fill = pickFill({ ...n, labels: n.labels, group: n.group, properties: n.properties });
      return {
        ...n,
        id: n.id,
        name: n.display_label || n.id,
        __fill: fill,
        __deg: degree.get(n.id) || 0,
        __primary: primaryLine(n),
        __typeLine: typeLine(n),
      };
    });

    const seenGroups = new Map();
    const findingSeverities = new Map();
    nodes.forEach((n) => {
      const key = n.group || (n.labels && n.labels[0]) || 'Node';
      if (!seenGroups.has(key)) {
        seenGroups.set(key, colorForGroup(key));
      }
      const labs = n.labels || [];
      if (labs.includes('Finding') && n.properties?.severity) {
        const sv = String(n.properties.severity).toLowerCase();
        if (SEVERITY_FILL[sv]) findingSeverities.set(sv, SEVERITY_FILL[sv]);
      }
    });

    const legendGroups = Array.from(seenGroups.entries()).map(([label, color]) => ({ label, color }));
    const legendSeverity = Array.from(findingSeverities.entries()).map(([label, color]) => ({ label, color }));

    return {
      data: { nodes, links },
      stats: {
        nodeCount: nodes.length,
        linkCount: links.length,
        isolated: nodes.filter((n) => (degree.get(n.id) || 0) === 0).length,
      },
      legendGroups,
      legendSeverity,
    };
  }, [graphData]);

  const handleClick = useCallback(
    (node) => {
      if (typeof onNodeClick === 'function') {
        onNodeClick(node);
      }
    },
    [onNodeClick]
  );

  const paintNode = useCallback(
    (node, ctx, globalScale) => {
      const isSel = highlightId && node.id === highlightId;
      const r = node.__deg > 0 ? 7 : 6;
      const x = node.x;
      const y = node.y;
      if (x === undefined || y === undefined) return;

      if (isSel) {
        ctx.beginPath();
        ctx.arc(x, y, r + 4, 0, 2 * Math.PI, false);
        ctx.strokeStyle = 'rgba(250, 204, 21, 0.95)';
        ctx.lineWidth = 2 / globalScale;
        ctx.stroke();
      }

      ctx.beginPath();
      ctx.arc(x, y, r, 0, 2 * Math.PI, false);
      ctx.fillStyle = node.__fill;
      ctx.fill();
      ctx.strokeStyle = 'rgba(15, 23, 42, 0.85)';
      ctx.lineWidth = 1.2 / globalScale;
      ctx.stroke();

      const fontPx = Math.max(10, Math.min(13, 12 / Math.max(0.35, globalScale)));
      ctx.font = `${fontPx}px ui-sans-serif, system-ui, sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'top';
      ctx.fillStyle = '#e2e8f0';
      const line1 = node.__primary || '';
      ctx.fillText(line1, x, y + r + 4 / globalScale);
      ctx.fillStyle = '#94a3b8';
      const subSize = Math.max(8, fontPx - 2);
      ctx.font = `${subSize}px ui-sans-serif, system-ui, sans-serif`;
      ctx.fillText(truncate(node.__typeLine, 40), x, y + r + 4 / globalScale + fontPx + 2);
    },
    [highlightId]
  );

  const nodeTooltip = useCallback((node) => {
    const props = node.properties || {};
    const sev = props.severity
      ? `<div style="color:#cbd5e1;margin-top:4px">Severity: <strong>${String(props.severity)}</strong></div>`
      : '';
    const phase = props.phase ? `<div style="color:#94a3b8;font-size:11px">${props.phase}</div>` : '';
    return `<div style="max-width:280px;text-align:left">
      <div style="font-weight:600;color:#f8fafc;margin-bottom:4px">${node.__primary || node.name}</div>
      <div style="color:#94a3b8;font-size:11px">${(node.labels || []).join(' · ') || node.group || ''}</div>
      ${sev}${phase}
      <div style="color:#64748b;font-size:11px;margin-top:6px">Connections: ${node.__deg}</div>
    </div>`;
  }, []);

  const zoomReset = useCallback(() => {
    const fg = fgRef.current;
    if (fg && typeof fg.zoomToFit === 'function') {
      fg.zoomToFit(400, 48);
    }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => zoomReset(), 350);
    return () => clearTimeout(t);
  }, [data.nodes.length, data.links.length, zoomReset]);

  useEffect(() => {
    const fg = fgRef.current;
    if (!fg || typeof fg.d3Force !== 'function') return;
    try {
      const ch = fg.d3Force('charge');
      if (ch && typeof ch.strength === 'function') ch.strength(-150);
      const lk = fg.d3Force('link');
      if (lk && typeof lk.distance === 'function') lk.distance(95);
    } catch {
      /* ignore */
    }
  }, [data]);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-3 text-xs text-muted-foreground">
        <div className="flex flex-wrap items-center gap-3">
          <span>
            <strong className="text-foreground">{stats.nodeCount}</strong> nodes ·{' '}
            <strong className="text-foreground">{stats.linkCount}</strong> links
            {stats.isolated > 0 && stats.linkCount === 0 && (
              <span className="text-amber-500"> — no relationships in this snapshot (nodes are findings or unrelated)</span>
            )}
            {stats.linkCount > 0 && stats.isolated > 0 && (
              <span className="text-muted-foreground"> · {stats.isolated} isolated</span>
            )}
          </span>
          <span className="hidden sm:inline-flex items-center gap-1 border-l border-border pl-3">
            <MousePointer2 className="h-3.5 w-3.5" aria-hidden />
            Hover for details · drag to move · scroll to zoom
          </span>
        </div>
        <Button type="button" variant="outline" size="sm" className="h-8 gap-1.5 text-xs" onClick={zoomReset}>
          <Maximize2 className="h-3.5 w-3.5" />
          Fit graph
        </Button>
      </div>

      {(legendGroups.length > 0 || legendSeverity.length > 0) && (
        <div className="space-y-2 rounded-md border border-border/80 bg-secondary/20 px-3 py-2">
          {legendGroups.length > 0 && (
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">Node types</span>
              {legendGroups.map(({ label, color }) => (
                <span key={label} className="inline-flex items-center gap-1.5 text-[11px] text-foreground">
                  <span className="inline-block h-2.5 w-2.5 shrink-0 rounded-full ring-1 ring-border" style={{ background: color }} />
                  {label}
                </span>
              ))}
            </div>
          )}
          {legendSeverity.length > 0 && (
            <div className="flex flex-wrap items-center gap-2 border-t border-border/60 pt-2">
              <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">Finding severity</span>
              {legendSeverity.map(({ label, color }) => (
                <span key={label} className="inline-flex items-center gap-1.5 text-[11px] capitalize text-foreground">
                  <span className="inline-block h-2.5 w-2.5 shrink-0 rounded-full ring-1 ring-border" style={{ background: color }} />
                  {label}
                </span>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="relative h-[min(560px,72vh)] w-full min-h-[360px] overflow-hidden rounded-lg border border-border bg-[#070b14] shadow-inner">
        {data.nodes.length === 0 ? (
          <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
            No nodes to display.
          </div>
        ) : (
          <ForceGraph2D
            ref={fgRef}
            graphData={data}
            nodeId="id"
            nodeLabel=""
            linkLabel="name"
            nodePointerAreaPaint={(node, color, ctx, globalScale) => {
              if (node.x === undefined || node.y === undefined) return;
              const gs = Math.max(0.35, globalScale || 1);
              const r = 44 / gs;
              ctx.fillStyle = color;
              ctx.beginPath();
              ctx.arc(node.x, node.y, r, 0, 2 * Math.PI, false);
              ctx.fill();
            }}
            nodeCanvasObjectMode={() => 'replace'}
            nodeCanvasObject={paintNode}
            nodeTooltip={nodeTooltip}
            linkDirectionalArrowLength={5}
            linkDirectionalArrowRelPos={1}
            linkWidth={1.5}
            linkColor={() => 'rgba(148, 163, 184, 0.45)'}
            linkDirectionalArrowColor={() => 'rgba(148, 163, 184, 0.55)'}
            linkTooltip={(link) =>
              `<div style="font-size:12px;color:#e2e8f0">Relationship: <strong style="color:#fff">${link.name || 'RELATED'}</strong></div>`
            }
            cooldownTicks={140}
            onNodeClick={handleClick}
            backgroundColor="#070b14"
            enableNodeDrag
            enableZoomInteraction
            enablePanInteraction
            minZoom={0.35}
            maxZoom={8}
            d3AlphaDecay={0.02}
            d3VelocityDecay={0.35}
            warmupTicks={80}
            onNodeHover={(node) => {
              document.body.style.cursor = node ? 'pointer' : 'default';
            }}
          />
        )}
      </div>
    </div>
  );
}
